import { Component, type ErrorInfo, type ReactNode } from "react";

const CHUNK_ERROR_RELOAD_KEY = "chunk_load_error_reload";
const RELOAD_COOLDOWN_MS = 10_000;

function isChunkLoadError(error: Error): boolean {
  const message = error.message.toLowerCase();

  return (
    message.includes("failed to fetch dynamically imported module") ||
    message.includes("loading chunk") ||
    message.includes("loading css chunk") ||
    message.includes("dynamically imported module")
  );
}

function handleChunkError(): void {
  const now = Date.now();
  const lastReload = sessionStorage.getItem(CHUNK_ERROR_RELOAD_KEY);

  if (lastReload && now - Number(lastReload) < RELOAD_COOLDOWN_MS) return;

  sessionStorage.setItem(CHUNK_ERROR_RELOAD_KEY, String(now));
  window.location.reload();
}

interface ChunkErrorBoundaryProps {
  children: ReactNode;
}

interface ChunkErrorBoundaryState {
  hasError: boolean;
}

export class ChunkErrorBoundary extends Component<
  ChunkErrorBoundaryProps,
  ChunkErrorBoundaryState
> {
  constructor(props: ChunkErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ChunkErrorBoundaryState | null {
    if (isChunkLoadError(error)) {
      handleChunkError();

      return { hasError: true };
    }

    return null;
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    if (!isChunkLoadError(error)) {
      console.error("Application error:", error, errorInfo);
    }
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return null;
    }

    return this.props.children;
  }
}

export function setupVitePreloadErrorHandler(): void {
  window.addEventListener("vite:preloadError", () => {
    handleChunkError();
  });
}
